import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { AuthState, onAuthUIStateChange } from '../helpr/authHelper';
import UserService from '../services/user-service';
import { queryClient } from '../helpr/queryClient';
import { useNavigate } from 'react-router-dom';

const IdentityContext = createContext({});
const IdentityDispatchContext = createContext({});

const IdentityProvider = ({ children }) => {
  const [identity, setIdentity] = useState({
    authState: null,
    modules: [],
    initialUser: null,
    user: null,
    role: null,
    defaultPath: null,
    pendingUsername: null,
    loading: true,
  });

  const loadingRef = useRef(false);
  const navigate = useNavigate();

  const setAuthState = useCallback((authState) => setIdentity(prev => ({ ...prev, authState })), []);
  const setUser = useCallback((user) => setIdentity(prev => ({ ...prev, user })), []);
  const setInitialUser = useCallback((initialUser) => setIdentity(prev => ({ ...prev, initialUser })), []);
  const setModules = useCallback((modules) => setIdentity(prev => ({ ...prev, modules })), []);
  const setRole = useCallback((role) => setIdentity(prev => ({ ...prev, role })), []);

  const clearIdentityContext = useCallback(() => {
    loadingRef.current = false;
    setIdentity({ authState: null, modules: [], initialUser: null, user: null, role: null, defaultPath: null, pendingUsername: null, loading: false });
  }, []);


  const loadUserData = useCallback(async (session) => {
    console.log('loadUserData called, loadingRef:', loadingRef.current);

    if (loadingRef.current) return;

    loadingRef.current = true;

    try {
      const attrs = session?.tokens?.idToken?.payload ?? {};
      const groups = attrs['cognito:groups'];
      const r = groups && groups.length ? groups[0] : '';

      const userService = new UserService();
      const mods = await userService.getUserModules(r, { attributes: attrs });

      const defaultModule = mods.find(m => m.default);

      setIdentity({
        authState: AuthState.SignedIn,
        user: { attributes: attrs },
        role: r,
        modules: mods,
        initialUser: null,
        defaultPath: defaultModule?.path || null,
        pendingUsername: null,
        loading: false,
      });

      const currentPath = window.location.pathname;
      const isOnValidModule = mods.some(m => currentPath === m.path || currentPath.startsWith(m.path + '/'));
      if (defaultModule?.path && !isOnValidModule) navigate(defaultModule.path, { replace: true });

      return defaultModule?.path || null;
    } finally {
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    getCurrentUser()
      .then(() => fetchAuthSession())
      .then(session => {
        console.log('IdentityProvider mount effect: calling loadUserData');
        return loadUserData(session);
      })
      .catch(() => setIdentity(prev => {
        if (prev.authState === AuthState.SignedIn) return prev;
        return { ...prev, authState: AuthState.SignIn, loading: false };
      }));

    const unsubscribe = onAuthUIStateChange(async (nextAuthState, authData) => {
      try { await queryClient.invalidateQueries(['calendarDates']); } catch {}

      if (nextAuthState === AuthState.ConfirmSignIn || nextAuthState === AuthState.TOTPSetup || nextAuthState === AuthState.ResetPassword) {
        setIdentity(prev => ({ ...prev, authState: nextAuthState, initialUser: authData, pendingUsername: authData?.username ?? prev.pendingUsername }));
      } else if (nextAuthState === AuthState.SignedIn) {
        loadUserData(authData);
      } else {
        setIdentity(prev => ({ ...prev, authState: nextAuthState }));
      }
    });

    return unsubscribe;
    // eslint-disable-next-line
  }, []);

  const dispatchValue = useMemo(
    () => ({ setAuthState, setUser, setInitialUser, setModules, setRole, clearIdentityContext, loadUserData }),
    // eslint-disable-next-line
    []
  );

  return (
    <IdentityContext.Provider value={{ authState: identity.authState, user: identity.user, initialUser: identity.initialUser, modules: identity.modules, role: identity.role, defaultPath: identity.defaultPath, pendingUsername: identity.pendingUsername, loading: identity.loading }}>
      <IdentityDispatchContext.Provider value={dispatchValue}>
        {children}
      </IdentityDispatchContext.Provider>
    </IdentityContext.Provider>
  );
};

export { IdentityProvider, IdentityContext, IdentityDispatchContext };
